import * as React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import i18n from "@/i18n";
import { isChunkLoadError } from "@/shared/lib/chunk-reload";

interface Props {
  children: React.ReactNode;
  /** Injectable for tests — jsdom's window.location is unforgeable. */
  reload?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Scoped error boundary for major route sections.
 * Catches render-time errors within a route subtree and shows a contained
 * fallback — keeping the sidebar and header intact so users can navigate away.
 */
export default class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("[RouteErrorBoundary]", error, info.componentStack);
    }
  }

  private handleRetry = () => {
    // A stale-chunk error can't be recovered by re-rendering: React.lazy
    // caches the rejected import, so only fetching the new index.html helps.
    if (isChunkLoadError(this.state.error)) {
      (this.props.reload ?? (() => window.location.reload()))();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 text-center p-8">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
          <AlertTriangle size={32} className="text-amber-500" />
        </div>

        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-primary">
            {i18n.t("common.src.errorRouteBoundary.somethingWentWrong")}</h2>
          <p className="text-sm text-secondary max-w-sm">
            {i18n.t("common.src.errorRouteBoundary.anUnexpectedError")}</p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors"
          >
            <RefreshCw size={14} />
            {i18n.t("common.src.errorRouteBoundary.tryAgain")}</button>
          <button
            type="button"
            onClick={() => (window.location.href = "/")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-strong text-secondary hover:bg-surface transition-colors"
          >
            <Home size={14} />
            {i18n.t("common.src.errorRouteBoundary.goHome")}</button>
        </div>

        {import.meta.env.DEV && this.state.error && (
          <pre className="mt-2 p-4 bg-surface rounded-lg text-left text-xs text-red-600 dark:text-red-400 max-w-lg overflow-auto">
            {this.state.error.toString()}
          </pre>
        )}
      </div>
    );
  }
}
